import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    useSidebarHeaderOffset
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    type ChartConfig,
    NCoreUIKitTheme,
    Sparkline,
    Separator,
    PieChart,
    Sidebar,
    TabBar,
    Avatar,
    Button,
    Badge,
    Chart,
    Card,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    BanknoteArrowUp,
    ArrowDownLeft,
    ShoppingCart,
    ArrowUpRight,
    ChartColumn,
    TrendingUp,
    CreditCard,
    EyeOff,
    House,
    Play,
    Plus,
    Bell,
    User,
    Zap,
    Car,
    Eye
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Transaction = {
    icon: NCoreUIKitIcon;
    amount: number;
    title: string;
    key: string;
};

const ShoppingCartIcon = createKitIcon(ShoppingCart);

const ArrowDownLeftIcon = createKitIcon(ArrowDownLeft);

const ArrowUpRightIcon = createKitIcon(ArrowUpRight);

const BanknoteIcon = createKitIcon(BanknoteArrowUp);

const TrendingUpIcon = createKitIcon(TrendingUp);

const CreditCardIcon = createKitIcon(CreditCard);

const ChartColumnIcon = createKitIcon(ChartColumn);

const EyeOffIcon = createKitIcon(EyeOff);

const HouseIcon = createKitIcon(House);

const ZapIcon = createKitIcon(Zap);

const PlayIcon = createKitIcon(Play);

const PlusIcon = createKitIcon(Plus);

const BellIcon = createKitIcon(Bell);

const CarIcon = createKitIcon(Car);

const UserIcon = createKitIcon(User);

const EyeIcon = createKitIcon(Eye);

const BALANCE_TREND = [
    46.4,
    46.9,
    46.6,
    47.3,
    47.1,
    47.8,
    48.2
];

const SPENDING = [
    {
        name: "market",
        value: 2180
    },
    {
        name: "bills",
        value: 2060
    },
    {
        name: "transport",
        value: 1140
    },
    {
        name: "fun",
        value: 1040
    }
];

const TRANSACTIONS: Array<Transaction> = [
    {
        title: "blocks.mobile01.txMarket",
        icon: ShoppingCartIcon,
        key: "txMarket",
        amount: -386.9
    },
    {
        title: "blocks.mobile01.txSalary",
        icon: BanknoteIcon,
        key: "txSalary",
        amount: 24500
    },
    {
        title: "blocks.mobile01.txTaxi",
        amount: -142.5,
        icon: CarIcon,
        key: "txTaxi"
    },
    {
        title: "blocks.mobile01.txStream",
        amount: -229.99,
        key: "txStream",
        icon: PlayIcon
    },
    {
        title: "blocks.mobile01.txUtility",
        key: "txUtility",
        amount: -412.3,
        icon: ZapIcon
    }
];

const stylesheet = StyleSheet.create({
    section: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    balance: {
        alignItems: "flex-end",
        flexDirection: "row"
    },
    trend: {
        alignSelf: "flex-start"
    },
    header: {
        alignItems: "center",
        flexDirection: "row"
    },
    action: {
        alignItems: "center",
        flex: 1
    },
    bar: {
        alignItems: "center",
        flexDirection: "row"
    },
    page: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    actions: {
        flexDirection: "row"
    },
    row: {
        flexDirection: "row"
    },
    greeting: {
        flexShrink: 1,
        flexGrow: 1
    },
    amount: {
        flexShrink: 1,
        flexGrow: 1
    },
    spark: {
        flexShrink: 1,
        flexGrow: 1
    },
    chart: {
        width: "100%"
    },
    column: {
        flexBasis: 0,
        minWidth: 0,
        flexGrow: 1
    },
    content: {
        flexGrow: 1
    },
    fill: {
        flexGrow: 1
    }
});

const handlePress = () => undefined;

const Mobile01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const headerOffset = useSidebarHeaderOffset({
        rowHeight: controlHeights.large
    });

    const {
        width
    } = useWindowDimensions();

    const isPhone = width < shapeConfigs.compactBreakpoint;

    const isSplit = width >= shapeConfigs.wideBreakpoint;

    const [
        isHidden,
        setIsHidden
    ] = useState(false);

    const [
        activeKey,
        setActiveKey
    ] = useState("home");

    const currency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const wholeCurrency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const signedCurrency = new Intl.NumberFormat(activeLocale, {
        signDisplay: "exceptZero",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long",
        day: "numeric",
        month: "long"
    });

    const spendingConfig: ChartConfig = {
        transport: {
            title: t("blocks.mobile01.categoryTransport"),
            colorIndex: 2
        },
        market: {
            title: t("blocks.mobile01.categoryMarket"),
            colorIndex: 1
        },
        bills: {
            title: t("blocks.mobile01.categoryBills"),
            colorIndex: 3
        },
        fun: {
            title: t("blocks.mobile01.categoryFun"),
            colorIndex: 4
        }
    };

    const quickActions = [
        {
            title: t("blocks.mobile01.send"),
            icon: ArrowUpRightIcon,
            key: "send"
        },
        {
            title: t("blocks.mobile01.request"),
            icon: ArrowDownLeftIcon,
            key: "request"
        },
        {
            title: t("blocks.mobile01.topUp"),
            icon: PlusIcon,
            key: "topUp"
        },
        {
            title: t("blocks.mobile01.pay"),
            icon: CreditCardIcon,
            key: "pay"
        }
    ];

    const tabs = [
        {
            title: t("blocks.mobile01.tabHome"),
            icon: HouseIcon,
            key: "home"
        },
        {
            title: t("blocks.mobile01.tabCards"),
            icon: CreditCardIcon,
            key: "cards"
        },
        {
            title: t("blocks.mobile01.tabInsights"),
            icon: ChartColumnIcon,
            key: "insights"
        },
        {
            title: t("blocks.mobile01.tabProfile"),
            icon: UserIcon,
            key: "profile"
        }
    ];

    const handleVisibilityToggle = () => setIsHidden(!isHidden);

    const handleTab = (key: string) => () => setActiveKey(key);

    const renderAction = (action: typeof quickActions[number]) => <View
        style={[
            stylesheet.action,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={action.key}
    >
        <Button
            accessibilityLabel={action.title}
            onPress={handlePress}
            icon={action.icon}
            variant="tonal"
            type="neutral"
            size="large"
        />
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {action.title}
        </Text>
    </View>;

    const renderActionButton = (action: typeof quickActions[number]) => <Button
        onPress={handlePress}
        title={action.title}
        icon={action.icon}
        variant="outline"
        key={action.key}
        type="neutral"
        size="small"
    />;

    const renderTransaction = (transaction: Transaction) => <Item
        onPress={handlePress}
        key={transaction.key}
        size="small"
    >
        <Item.Media
            icon={transaction.icon}
            variant="icon"
        />
        <Item.Content>
            <Item.Title>
                {t(transaction.title)}
            </Item.Title>
            <Item.Description>
                {t(`${transaction.title}Detail`)}
            </Item.Description>
        </Item.Content>
        <Text
            color={transaction.amount > 0 ? "success" : "high"}
            variant="labelMediumSize"
        >
            {signedCurrency.format(transaction.amount)}
        </Text>
    </Item>;

    const renderNavItem = (tab: typeof tabs[number]) => <Sidebar.MenuItem
        key={tab.key}
    >
        <Sidebar.MenuButton
            isActive={activeKey === tab.key}
            onPress={handleTab(tab.key)}
            title={tab.title}
            icon={tab.icon}
        />
    </Sidebar.MenuItem>;

    const greeting = <View
        style={stylesheet.greeting}
    >
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {dateFormat.format(new Date(2026, 10, 1))}
        </Text>
        <Text
            variant="titleMediumSize"
            color="high"
        >
            {t("blocks.mobile01.greeting")}
        </Text>
    </View>;

    const bell = <Badge.Indicator
        type="danger"
        count={2}
    >
        <Button
            accessibilityLabel={t("blocks.mobile01.notifications")}
            onPress={handlePress}
            variant="ghost"
            icon={BellIcon}
            type="neutral"
        />
    </Badge.Indicator>;

    const balanceCard = <Card
        spreadBehaviour="stretch"
        variant="outline"
    >
        <Card.Header>
            <Card.Description>
                {t("blocks.mobile01.balance")}
            </Card.Description>
            <Card.Title
                isMetric={true}
            >
                {isHidden ? t("blocks.mobile01.hiddenBalance") : currency.format(48215.4)}
            </Card.Title>
            <Card.Action>
                <Button
                    accessibilityLabel={t(isHidden ? "blocks.mobile01.showBalance" : "blocks.mobile01.hideBalance")}
                    icon={isHidden ? EyeIcon : EyeOffIcon}
                    onPress={handleVisibilityToggle}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </Card.Action>
        </Card.Header>
        <Card.Content>
            <View
                style={[
                    stylesheet.balance,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.amount,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Badge
                        title={t("blocks.mobile01.weekChange", [
                            wholeCurrency.format(1240)
                        ])}
                        style={stylesheet.trend}
                        icon={TrendingUpIcon}
                        variant="tonal"
                        type="success"
                    />
                </View>
                <View
                    style={stylesheet.spark}
                >
                    <Sparkline
                        summary={t("blocks.mobile01.balanceSummary")}
                        values={BALANCE_TREND}
                        size="medium"
                    />
                </View>
            </View>
        </Card.Content>
        {
            isPhone ?
                null
            :
                <Card.Footer
                    align="stretch"
                >
                    {quickActions.map(renderActionButton)}
                </Card.Footer>
        }
    </Card>;

    const spendingChart = <Chart
        description={t("blocks.mobile01.spendingDescription")}
        style={isPhone ? stylesheet.chart : stylesheet.fill}
        title={t("blocks.mobile01.spending")}
        spreadBehaviour="stretch"
        isAnimatedOnMount={true}
        config={spendingConfig}
        size="small"
    >
        <PieChart
            valueFormatter={(value) => wholeCurrency.format(value)}
            categoryTitle={t("blocks.mobile01.category")}
            centerValue={wholeCurrency.format(6420)}
            centerTitle={t("blocks.mobile01.total")}
            categoryKey="name"
            plotSize="small"
            valueKey="value"
            variant="donut"
            data={SPENDING}
        />
    </Chart>;

    const allButton = <Button
        title={t("blocks.mobile01.all")}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
    />;

    if(!isPhone) {
        return <Sidebar.Provider
            variant="inset"
        >
            <Sidebar
                title={t("blocks.mobile01.tabsTitle")}
                collapseBehaviour="icon"
            >
                <Sidebar.Header>
                    <Sidebar.MenuButton
                        renderIcon={() => <Avatar
                            title={t("blocks.mobile01.userName")}
                            size="xSmall"
                        />}
                        subTitle={t("blocks.mobile01.account")}
                        title={t("blocks.mobile01.userName")}
                        style={{
                            marginTop: headerOffset
                        }}
                        onPress={handlePress}
                        size="large"
                    />
                </Sidebar.Header>
                <Sidebar.Content>
                    <Sidebar.Group>
                        <Sidebar.Menu>
                            {tabs.map(renderNavItem)}
                        </Sidebar.Menu>
                    </Sidebar.Group>
                </Sidebar.Content>
            </Sidebar>
            <Sidebar.Inset>
                <View
                    style={[
                        stylesheet.bar,
                        {
                            paddingHorizontal: spaces.spacingLg,
                            paddingVertical: spaces.spacingMd,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Sidebar.Trigger/>
                    {greeting}
                    {bell}
                </View>
                <Separator/>
                <View
                    style={[
                        stylesheet.content,
                        {
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    {balanceCard}
                    <View
                        style={[
                            isSplit ? stylesheet.row : undefined,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={isSplit ? stylesheet.column : undefined}
                        >
                            {spendingChart}
                        </View>
                        <View
                            style={isSplit ? stylesheet.column : undefined}
                        >
                            <Card
                                spreadBehaviour="stretch"
                                style={stylesheet.fill}
                                variant="outline"
                            >
                                <Card.Header>
                                    <Card.Title>
                                        {t("blocks.mobile01.recent")}
                                    </Card.Title>
                                    <Card.Action>
                                        {allButton}
                                    </Card.Action>
                                </Card.Header>
                                <Card.Content>
                                    <Item.Group>
                                        {TRANSACTIONS.map(renderTransaction)}
                                    </Item.Group>
                                </Card.Content>
                            </Card>
                        </View>
                    </View>
                </View>
            </Sidebar.Inset>
        </Sidebar.Provider>;
    }

    return <View
        style={[
            stylesheet.page,
            {
                maxWidth: shapeConfigs.dialogMaxWidth
            }
        ]}
    >
        <View
            style={[
                stylesheet.content,
                {
                    padding: spaces.spacingMd,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.header,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Avatar
                    title={t("blocks.mobile01.userName")}
                    size="small"
                />
                {greeting}
                {bell}
            </View>
            {balanceCard}
            <View
                style={[
                    stylesheet.actions,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {quickActions.map(renderAction)}
            </View>
            {spendingChart}
            <View
                style={{
                    gap: spaces.spacingSm
                }}
            >
                <View
                    style={stylesheet.section}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {t("blocks.mobile01.recent")}
                    </Text>
                    {allButton}
                </View>
                <Item.Group>
                    {TRANSACTIONS.map(renderTransaction)}
                </Item.Group>
            </View>
        </View>
        <TabBar
            title={t("blocks.mobile01.tabsTitle")}
            onChange={setActiveKey}
            labelBehaviour="always"
            activeKey={activeKey}
            items={tabs}
        />
    </View>;
};
export default Mobile01;
